/**
 * The "so what" strip.
 *
 * A billing page full of accurate numbers still leaves the admin to work out
 * whether anything needs doing. These cards do that step out loud — state the
 * position, say what it costs, and where there is a next step, link to it.
 * Capped at three: a rail of six insights is read as decoration.
 */
import { AlertTriangle, ArrowRight, CheckCircle2, Info, TrendingUp } from 'lucide-react'

const TONES = {
  critical: {
    icon: AlertTriangle,
    bar: 'bg-rose-500 dark:bg-rose-400',
    chip: 'bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400',
  },
  warning: {
    icon: TrendingUp,
    bar: 'bg-amber-500 dark:bg-amber-400',
    chip: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
  },
  good: {
    icon: CheckCircle2,
    bar: 'bg-emerald-500 dark:bg-emerald-400',
    chip: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
  },
  neutral: {
    icon: Info,
    bar: 'bg-gray-300 dark:bg-gray-600',
    chip: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400',
  },
}

export default function InsightRail({ insights, onAction }) {
  if (!insights?.length) return null

  // Literal class names so Tailwind's scanner keeps all three: a lone insight
  // stretched across a third of the page reads as a card that failed to load.
  const columns = { 1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3' }[insights.length] || 'lg:grid-cols-3'

  return (
    <div className={`grid grid-cols-1 ${columns} gap-4`}>
      {insights.map((insight, i) => {
        const tone = TONES[insight.tone] || TONES.neutral
        const Icon = tone.icon
        return (
          <div
            key={insight.id}
            className="relative overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800
                       bg-white dark:bg-gray-900 p-4 pl-5 animate-card-in
                       transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-700 hover:shadow-sm"
            style={{ animationDelay: `${260 + i * 60}ms` }}
          >
            <span className={`absolute left-0 inset-y-0 w-1 ${tone.bar}`} />
            <div className="flex items-start gap-3">
              <span className={`p-1.5 rounded-lg flex-shrink-0 ${tone.chip}`}>
                <Icon className="w-3.5 h-3.5" />
              </span>
              <div className="min-w-0">
                <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                  {insight.title}
                </h3>
                <p className="text-[11.5px] text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                  {insight.body}
                </p>
                {insight.action && (
                  <button
                    type="button"
                    onClick={() => onAction?.(insight.actionKey)}
                    className="group mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold
                               text-navy-700 dark:text-navy-300 hover:gap-2 transition-all
                               focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
                  >
                    {insight.action}
                    <ArrowRight className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
